import {
  MarketRegistry,
  SideRules,
  Market,
  MarketCatalog,
  SymbolsIndex
} from './types';
import { MarketSelectorsData } from '../types';
import TokenManager from '../../../utils/tokenManager';

/* ======Helpers === === */

const toMarketId = (base: string, quote: string) => `${base}${quote}`;
const pushUnique = (arr: string[], v: string) => {
  if (!arr.includes(v)) arr.push(v);
};

/** Pull Tier commission default exactly as legacy normalizer does */
function getDefaultCommissionFraction(): number {
  try {
    const profile = (TokenManager as any)?.parsed()?.accessToken || {};
    const tier = Number(profile?.TierOtcPercent);
    // legacy default: 0.003 (== 0.3%)
    return Number.isFinite(tier) && tier > 0 ? tier : 0.35;
  } catch {
    return 0.35;
  }
}

/** ====== Normalizer (API -> MarketCatalog) ====== */
/**
 * Build a market registry from the Selector API payload.
 * - Preserves buy/sell side flags, visibility, min/max, desc, defaults
 * - Merges duplicate appearances of the same market across groups
 * - Builds base/quote indexes
 * - Captures symbol metadata (nameFa/nameEn/logo/order)
 * - Optional timing (for perf measurement in dev)
 */
export function buildMarketCatalog(
  selectors: MarketSelectorsData[] | undefined,
  opts?: { enableTiming?: boolean; label?: string }
): MarketCatalog {
  const enableTiming = !!opts?.enableTiming;
  const t0 = enableTiming ? performance.now() : 0;

  const registry: MarketRegistry = {
    byId: Object.create(null),
    ids: [],
    idsByBase: Object.create(null),
    idsByQuote: Object.create(null)
  };
  const symbols: SymbolsIndex = Object.create(null);

  if (!Array.isArray(selectors) || selectors.length === 0) {
    return { registry, symbols, timingMs: enableTiming ? 0 : undefined };
  }

  const defaultCommissionFraction = getDefaultCommissionFraction();

  for (const group of selectors) {
    // group-level asset metadata (usually the "pay" asset in legacy)
    if (group?.symbol) {
      if (!symbols[group.symbol]) {
        symbols[group.symbol] = {
          symbol: group.symbol,
          nameFa: (group as any).NameFa ?? group.NameFa,
          nameEn: (group as any).NameEn ?? group.NameEn,
          logo: (group as any).logo,
          order: (group as any).order ?? undefined
        };
      }
    }

    const subs = (group as any)?.SubSelector as any[] | undefined;
    // eslint-disable-next-line no-continue
    if (!subs?.length) continue;

    for (const s of subs) {
      const marketInfo = s?.MarketInfo;
      const marketStr: string | undefined =
        s?.Market || marketInfo?.Market || undefined;

      if (!marketInfo || !marketInfo.Base || !marketInfo.Quote) {
        // malformed row; keep behavior similar to legacy: warn & skip
        console.warn(
          'marketNormalizer: missing MarketInfo/Base/Quote for subselector',
          s
        );
        // eslint-disable-next-line no-continue
        continue;
      }

      // capture symbol meta for sub asset too (often the "other side")
      if (s?.symbol && !symbols[s.symbol]) {
        symbols[s.symbol] = {
          symbol: s.symbol,
          nameFa: s.nameFa ?? s.NameFa,
          nameEn: s.nameEn ?? s.NameEn,
          logo: s.logo,
          order: s.order ?? undefined
        };
      }

      const base = marketInfo.Base as string;
      const quote = marketInfo.Quote as string;
      const id = marketStr || toMarketId(base, quote);

      const commissionFraction =
        marketInfo.commissionPercentage !== undefined &&
        marketInfo.commissionPercentage !== -1
          ? Number(marketInfo.commissionPercentage) / 100
          : defaultCommissionFraction / 100;

      const buy: SideRules = {
        enabled: !!marketInfo.BuyStatus,
        visible: !!marketInfo.BuyVisibility,
        min: marketInfo.MinBuy ?? '0',
        max: marketInfo.MaxBuy ?? '0',
        desc: marketInfo.BuyDesc,
        defaultBaseValue: marketInfo.DefaultBaseValue,
        defaultQuoteValue: marketInfo.DefaultQuoteValue
      };

      const sell: SideRules = {
        enabled: !!marketInfo.SellStatus,
        visible: !!marketInfo.SellVisibility,
        min: marketInfo.MinSell ?? '0',
        max: marketInfo.MaxSell ?? '0',
        desc: marketInfo.SellDesc,
        defaultBaseValue: marketInfo.DefaultBaseValue,
        defaultQuoteValue: marketInfo.DefaultQuoteValue
      };

      const draft: Market = {
        id,
        base,
        quote,
        precisions: {
          base: Number(marketInfo.BasePrecision ?? 0),
          quote: Number(marketInfo.QuotePrecision ?? 0),
          priceTick: Number(marketInfo.Tick ?? 0)
        },
        fees: {
          commissionFraction,
          commissionPct: commissionFraction * 100 // keep both forms
        },
        buy,
        sell,
        status: buy.enabled || sell.enabled ? 'online' : 'offline',
        order: s.order ?? (group as any)?.order ?? undefined,
        sourceIds: [s.id].filter(Boolean)
      };

      // merge if we’ve already seen this market
      const existing = registry.byId[id];
      if (!existing) {
        registry.byId[id] = draft;
        registry.ids.push(id);
      } else {
        // merge side rules (prefer truthy/defined values)
        registry.byId[id] = {
          ...existing,
          precisions: {
            base: existing.precisions.base || draft.precisions.base,
            quote: existing.precisions.quote || draft.precisions.quote,
            priceTick:
              existing.precisions.priceTick || draft.precisions.priceTick
          },
          fees: {
            commissionFraction:
              existing.fees.commissionFraction ?? draft.fees.commissionFraction,
            commissionPct:
              existing.fees.commissionPct ?? draft.fees.commissionPct
          },
          buy: {
            enabled: existing.buy.enabled || draft.buy.enabled,
            visible: existing.buy.visible || draft.buy.visible,
            min: existing.buy.min || draft.buy.min,
            max: existing.buy.max || draft.buy.max,
            desc: existing.buy.desc || draft.buy.desc,
            defaultBaseValue:
              existing.buy.defaultBaseValue || draft.buy.defaultBaseValue,
            defaultQuoteValue:
              existing.buy.defaultQuoteValue || draft.buy.defaultQuoteValue
          },
          sell: {
            enabled: existing.sell.enabled || draft.sell.enabled,
            visible: existing.sell.visible || draft.sell.visible,
            min: existing.sell.min || draft.sell.min,
            max: existing.sell.max || draft.sell.max,
            desc: existing.sell.desc || draft.sell.desc,
            defaultBaseValue:
              existing.sell.defaultBaseValue || draft.sell.defaultBaseValue,
            defaultQuoteValue:
              existing.sell.defaultQuoteValue || draft.sell.defaultQuoteValue
          },
          status:
            existing.buy.enabled ||
            existing.sell.enabled ||
            draft.buy.enabled ||
            draft.sell.enabled
              ? 'online'
              : 'offline',
          order: Math.min(
            existing.order ?? Number.POSITIVE_INFINITY,
            draft.order ?? Number.POSITIVE_INFINITY
          ),
          sourceIds: Array.from(
            new Set([...(existing.sourceIds ?? []), ...(draft.sourceIds ?? [])])
          )
        };
      }

      // build indexes
      if (!registry.idsByBase[base]) registry.idsByBase[base] = [];
      if (!registry.idsByQuote[quote]) registry.idsByQuote[quote] = [];
      pushUnique(registry.idsByBase[base], id);
      pushUnique(registry.idsByQuote[quote], id);
    }
  }

  // stable sort ids by "order" if present, otherwise keep insertion order
  registry.ids.sort((a, b) => {
    const oa = registry.byId[a].order ?? Number.MAX_SAFE_INTEGER;
    const ob = registry.byId[b].order ?? Number.MAX_SAFE_INTEGER;
    return oa - ob;
  });

  const t1 = enableTiming ? performance.now() : 0;
  return {
    registry,
    symbols,
    timingMs: enableTiming ? t1 - t0 : undefined
  };
}

/** Convenience wrapper if you just want the registry (no symbol meta, no timing) */
export function buildMarketRegistry(
  selectors: MarketSelectorsData[] | undefined
): MarketRegistry {
  return buildMarketCatalog(selectors).registry;
}
